import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import {
  buildBreadcrumbSchema,
  buildServiceListSchema,
  buildSpeakableSchema,
} from "@/lib/schema";
import {
  NAATI_KEYWORDS_TOTAL,
  NAATI_KEYWORDS_EN_TOTAL,
  NAATI_FAQ_EN_TOTAL,
  NAATI_FAQ_TH_TOTAL,
} from "@/content/naati-corpus/counts.gen";
import { NAATI_FAQ_CATEGORIES } from "@/content/naati-corpus/faq-defs";
import { NAATI_VISA_META, NAATI_DOC_META, NAATI_PROVINCE_META } from "@/content/naati-corpus/meta.gen";

export const Route = createFileRoute("/en/naati/")({
  component: NaatiHubEN,
  loader: async () => {
    const { NAATI_KEYWORDS_EN_TOP, NAATI_FAQ_EN_TOP } = await import("@/content/naati-corpus/top.gen");
    return {
      keywords: NAATI_KEYWORDS_EN_TOP.slice(0, 120),
      faqs: NAATI_FAQ_EN_TOP.slice(0, 10),
    };
  },
  head: ({ loaderData }) => {
    const site = getActiveSiteUrl();
    const title = "NAATI Certified Translation Thailand 2026 — Australia & NZ Visa";
    const description = `NAATI-certified Thai ↔ English translation for Australian DHA, Immigration NZ, IRCC and UK Home Office. ${NAATI_KEYWORDS_EN_TOTAL}+ keywords, ${NAATI_FAQ_EN_TOTAL}+ FAQs, Partner/Student/Skilled/Tourist visa workflows — 2026 pricing.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/en/naati",
        type: "website",
        alternates: [{ lang: "th", path: "/naati" }],
        keywords: (loaderData?.keywords ?? []).slice(0, 80),
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "NAATI Translation", url: `${site}/en/naati` },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildServiceListSchema([
              { name: "NAATI Certified Translation (Thai ↔ English)", description: "DHA / Immigration NZ / IRCC accepted, credentialed CPN translators", url: "/en/services/certified-translation" },
              { name: "Partner Visa (309/820/100/801) Document Pack", description: "Marriage cert, birth cert, PCC — full NAATI pack", url: "/en/services/marriage-thailand" },
              { name: "Student & Skilled Visa Translation", description: "Transcripts, degrees, employment letters, bank statements", url: "/en/services/certified-translation" },
              { name: "Apostille & MFA Bundle", description: "NAATI + MFA legalisation in one hand-off", url: "/en/services/mfa-legalization" },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/en/naati")) },
      ],
    };
  },
});

function NaatiHubEN() {
  const { keywords, faqs } = Route.useLoaderData();
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-5xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span> <span>NAATI Translation</span>
        </nav>

        <header className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-3">
            NAATI Certified Translation in Thailand — 2026 Complete Guide
          </h1>
          <p className="text-muted-foreground text-lg">
            NAATI-credentialed Thai ↔ English translation for the Australian Department of Home Affairs,
            Immigration NZ, IRCC and the UK Home Office. Partner, Student, Skilled, PR and Tourist visas —
            Bangkok · Phuket · Chiang Mai · nationwide courier · online digital-stamp option.
          </p>
          <div className="mt-4 flex flex-wrap gap-2 text-sm">
            <a href="/en/services/certified-translation" className="rounded-md bg-primary px-4 py-2 text-primary-foreground font-medium">See services →</a>
            <a href="/contact" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Free consult</a>
            <a href="/naati" className="rounded-md border px-4 py-2 text-sm text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
          </div>
        </header>

        <section aria-label="Deep-dive guide" className="mb-10 rounded-lg border bg-primary/5 p-6">
          <h2 className="text-xl font-bold mb-2">Deep-dive: NAATI Translation Thailand 2026</h2>
          <p className="text-sm text-muted-foreground mb-3">
            Read our complete guide — case-officer rejections we resolve, the 100-point ID rule, sequencing with MFA legalisation, realistic THB pricing.
          </p>
          <a href="/en/insights/naati-certified-translation-thailand-australia-visa" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium text-sm">
            Read the full guide →
          </a>
        </section>

        <section aria-label="Top questions" className="mb-10">
          <h2 className="text-xl font-bold mb-3">All NAATI FAQ topics</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
            {NAATI_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/en/naati/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleEn} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descEn}</div>
              </a>
            ))}
          </div>
          <p className="mb-6 text-sm">
            <a href="/en/naati/faq" className="text-primary underline">Browse the full NAATI FAQ →</a>
          </p>
          <h2 className="text-xl font-bold mb-3">Top 10 Questions (English)</h2>
          <div className="space-y-3">
            {faqs.map((item, i) => (
              <details key={i} className="rounded-lg border bg-card p-4">
                <summary className="cursor-pointer font-semibold text-sm">
                  <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
                </summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section aria-label="NAATI by visa subclass" className="mb-10">
          <h2 className="text-xl font-bold mb-3">NAATI translation by visa subclass</h2>
          <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {NAATI_VISA_META.map((v) => (
              <a
                key={v.slug}
                href={`/en/naati/visa/${v.slug}`}
                className="rounded-lg border bg-card px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5"
              >
                {v.nameEn} →
              </a>
            ))}
          </div>
        </section>

        <section aria-label="NAATI by document type" className="mb-10">
          <h2 className="text-xl font-bold mb-3">NAATI translation by document type</h2>
          <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
            {NAATI_DOC_META.map((d) => (
              <a
                key={d.slug}
                href={`/en/naati/document/${d.slug}`}
                className="rounded-lg border bg-card px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5"
              >
                {d.titleEn.replace("NAATI Translation of ", "")} →
              </a>
            ))}
          </div>
        </section>

        <section aria-label="Keyword universe" className="mb-10">
          <h2 className="text-xl font-bold mb-3">NAATI translation by province</h2>
          <div className="mb-8 grid grid-cols-2 gap-2 text-sm md:grid-cols-3">
            {NAATI_PROVINCE_META.map((p) => (
              <a
                key={p.slug}
                href={`/en/naati/province/${p.slug}`}
                className="rounded-lg border bg-card px-3 py-2 transition-colors hover:border-primary hover:bg-primary/5"
              >
                NAATI {p.provinceEn} →
              </a>
            ))}
          </div>
          <h2 className="text-xl font-bold mb-3">Keyword coverage ({NAATI_KEYWORDS_TOTAL} total)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {keywords.map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">Ready to file your Australia visa in 3-5 days?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Free consult with our NAATI liaison + licensed Thai attorney — Mon-Sat 9-18 ICT
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">Contact us →</a>
        </section>

        <p className="mt-6 text-xs text-muted-foreground">
          Includes {NAATI_FAQ_TH_TOTAL} Thai-language FAQs at <a href="/naati" className="text-primary underline">/naati</a>.
        </p>
        <FaqSourceNote topic="translation" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}